/**
 * Page Registry
 *
 * Registry untuk komponen halaman yang bisa di-render sebagai tab.
 * Setiap halaman didefinisikan dengan path, label, komponen, dan level akses.
 */

import { lazy, Suspense, type ReactNode, type LazyExoticComponent, type ComponentType } from 'react';
import { Spin } from 'antd';
import { ValidasiSaldoAkhir_Path } from '@/modules/buku-pengawasan/validasi-saldo-akhir/types';
import {
  Home,
  BookOpen,
  Handshake,
  UserSquare2,
  Users,
  Building2,
  FileText,
  Briefcase,
  Banknote,
  Landmark,
  CreditCard,
  Coins,
  ClipboardList,
  Receipt,
  Calendar,
  ArrowDownCircle,
  ArrowUpCircle,
  ArrowLeftRight,
  Package,
  Calculator,
  Wallet,
  PieChart,
  Settings,
  User,
} from 'lucide-react';
import {
  LevelUser_09_SuperUser,
  LevelUser_03_Direktur,
  LevelUser_01_Operator,
} from '@/modules/data/user/types';
import { PageHeader } from '@/components/layout/PageHeader';

// ============================================
// LAZY LOAD PAGES
// ============================================

// Dashboard
const DashboardPage = lazy(() => import('@/modules/dashboard/DashboardPage'));

// Data Master
const CoaManagementPage = lazy(() => import('@/modules/data/coa/CoaManagementPage'));
const LawanTransaksiManagementPage = lazy(() => import('@/modules/data/lawan-transaksi/LawanTransaksiManagementPage'));
const KaryawanManagementPage = lazy(() => import('@/modules/data/karyawan/KaryawanManagementPage'));
const UserManagementPage = lazy(() => import('@/modules/data/user/UserManagementPage'));
const TokoManagementPage = lazy(() => import('@/modules/data/toko/TokoManagementPage'));
const PemegangSahamPage = lazy(() => import('@/modules/data/pemegang-saham/PemegangSahamPage'));
const DataProjectManagementPage = lazy(() => import('@/modules/data/project/DataProjectManagementPage'));

// Jurnal
const JurnalUmumPage = lazy(() => import('@/modules/jurnal/jurnal-umum/JurnalUmumPage'));

// Adjustment Akhir Bulan — 1 halaman 4-section (Penyusutan/Amortisasi/Forex/HPP).
// Menggantikan 4 user control terpisah di Booku Lama.
// Lihat .claude/docs/Booku v2/jurnal-akuntansi/adjustment-akhir-bulan.md
const AdjustmentAkhirBulanPage = lazy(() =>
  import('@/modules/jurnal/adjustment-akhir-bulan').then(m => ({ default: m.AdjustmentAkhirBulanPage }))
);

// Buku Pengawasan
const PemindahbukuanPage = lazy(() => import('@/modules/buku-pengawasan/pemindahbukuan/PemindahbukuanPage'));
const DepositOperasionalPage = lazy(() => import('@/modules/buku-pengawasan/deposit-operasional/DepositOperasionalPage'));
const BuktiPenerimaanPage = lazy(() => import('@/modules/buku-pengawasan/penerimaan-pengeluaran/bukti-penerimaan/BuktiPenerimaanPage'));
const BuktiPengeluaranPage = lazy(() => import('@/modules/buku-pengawasan/penerimaan-pengeluaran/bukti-pengeluaran/BuktiPengeluaranPage'));

// Pengaturan
const CompanyProfilePage = lazy(() => import('@/modules/file/CompanyProfilePage'));

// User
const ProfilPage = lazy(() => import('@/modules/data/user/ProfilPage'));

// Laporan - Buku Besar
const BukuBesarHost = lazy(() => import('@/modules/buku-besar').then(m => ({ default: m.BukuBesarHost })));
const BukuKasHost = lazy(() => import('@/modules/buku-besar').then(m => ({ default: m.BukuKasHost })));
const BukuBankHost = lazy(() => import('@/modules/buku-besar').then(m => ({ default: m.BukuBankHost })));
const BukuCashAdvanceHost = lazy(() => import('@/modules/buku-besar').then(m => ({ default: m.BukuCashAdvanceHost })));
const BukuPettyCashHost = lazy(() => import('@/modules/buku-besar').then(m => ({ default: m.BukuPettyCashHost })));

// Pembelian - Penjualan
const POPembelianPage = lazy(() => import('@/modules/pembelian-penjualan/pembelian/po').then(m => ({ default: m.POPembelianPage })));
const POPenjualanPage = lazy(() => import('@/modules/pembelian-penjualan/penjualan/po').then(m => ({ default: m.POPenjualanPage })));
const SJPembelianPage = lazy(() => import('@/modules/pembelian-penjualan/pembelian/sj/SJPembelianPage'));
const BASTPembelianPage = lazy(() => import('@/modules/pembelian-penjualan/pembelian/bast/BASTPembelianPage'));
const SJPenjualanPage = lazy(() => import('@/modules/pembelian-penjualan/penjualan/sj/SJPenjualanPage'));
const BASTPenjualanPage = lazy(() => import('@/modules/pembelian-penjualan/penjualan/bast/BASTPenjualanPage'));

// Buku Pengawasan
// Hutang Usaha - Multi-Tab Internal Pattern (1 page dengan 10 tab internal)
const HutangUsahaPage = lazy(() => import('@/modules/buku-pengawasan/hutang-usaha').then(m => ({ default: m.HutangUsahaPage })));

// Hutang-Piutang Karyawan
const PiutangKaryawanPage = lazy(() => import('@/modules/buku-pengawasan/hutang-piutang-karyawan/piutang').then(m => ({ default: m.PiutangKaryawanPage })));
const HutangKaryawanPage = lazy(() => import('@/modules/buku-pengawasan/hutang-piutang-karyawan/hutang').then(m => ({ default: m.HutangKaryawanPage })));

// Hutang-Piutang Pemegang Saham
const HutangPemegangSahamPage = lazy(() => import('@/modules/buku-pengawasan/hutang-piutang-pemegang-saham/hutang').then(m => ({ default: m.HutangPemegangSahamPage })));
const PiutangPemegangSahamPage = lazy(() => import('@/modules/buku-pengawasan/hutang-piutang-pemegang-saham/piutang').then(m => ({ default: m.PiutangPemegangSahamPage })));

// Hutang Bank/Leasing - Multi-Tab Host Pattern (2 tab aplikasi terpisah)
const HutangBankPage = lazy(() => import('@/modules/buku-pengawasan/hutang-bank-leasing').then(m => ({ default: m.HutangBankPage })));
const HutangLeasingPage = lazy(() => import('@/modules/buku-pengawasan/hutang-bank-leasing').then(m => ({ default: m.HutangLeasingPage })));

// Validasi Saldo Awal (dashboard agregator Bar Saldo seluruh Buku Pengawasan)
const ValidasiSaldoAkhirPage = lazy(() =>
  import('@/modules/buku-pengawasan/validasi-saldo-akhir').then(m => ({ default: m.ValidasiSaldoAkhirPage }))
);
const ValidasiSaldoAwalPage = lazy(() =>
  import('@/modules/buku-pengawasan/validasi-saldo-awal').then(m => ({ default: m.ValidasiSaldoAwalPage }))
);

// Neraca Lajur (Laporan) — Trial Balance dengan gate kesesuaian saldo
// Lihat .claude/docs/Booku v2/laporan/neraca-lajur.md
const NeracaLajurPage = lazy(() =>
  import('@/modules/laporan/neraca-lajur').then(m => ({ default: m.NeracaLajurPage }))
);

// Laporan Laba Rugi Bulanan — matrix 12 bulan + Total (sumber data: tbl_coa.Saldo_{Bulan})
// Lihat .claude/docs/Booku v2/laporan/laba-rugi-bulanan.md
const LabaRugiBulananPage = lazy(() =>
  import('@/modules/laporan/laba-rugi-bulanan').then(m => ({ default: m.LabaRugiBulananPage }))
);

// Laporan HPP — struktur biaya produksi → HPP, 15 kolom (sumber data: tbl_coa.Saldo_{Bulan})
// Lihat .claude/docs/Booku v2/laporan/laporan-hpp.md
const LaporanHppPage = lazy(() =>
  import('@/modules/laporan/hpp').then(m => ({ default: m.LaporanHppPage }))
);

// Laporan Neraca Bulanan — posisi keuangan per akhir bulan (AKTIVA = PASSIVA)
// Sumber data: tbl_coa.Saldo_* + tbl_kursakhirbulan (konversi MUA → IDR)
// Lihat .claude/docs/Booku v2/laporan/laporan-neraca-bulanan.md
const LaporanNeracaBulananPage = lazy(() =>
  import('@/modules/laporan/neraca-bulanan').then(m => ({ default: m.LaporanNeracaBulananPage }))
);

// Tutup Buku Tahunan — tutup tahun aktif & buka tahun berikutnya (+ Reset untuk pengujian)
const TutupBukuTahunanPage = lazy(() =>
  import('@/modules/tutup-buku-tahunan').then(m => ({ default: m.TutupBukuTahunanPage }))
);

// Migrasi COA 9 Digit — sekali seumur hidup per perusahaan (Direktur ke atas)
const MigrasiCoaPage = lazy(() =>
  import('@/modules/migrasi-coa').then(m => ({ default: m.MigrasiCoaPage }))
);

// Validasi Saldo Awal — mode Data Awal (tahun LAMPAU)
const DataAwalValidasiSaldoAwalPage = lazy(() =>
  import('@/modules/buku-pengawasan/validasi-saldo-awal').then(m => {
    const { ValidasiSaldoAwalPage: Page, PageMode_DataAwal } = m;
    const WrapperComponent = () => <Page pageMode={PageMode_DataAwal} />;
    return { default: WrapperComponent };
  })
);

// Hutang Pihak Ketiga
const HutangPihakKetigaPage = lazy(() => import('@/modules/buku-pengawasan/hutang-pihak-ketiga').then(m => ({ default: m.HutangPihakKetigaPage })));

// Hutang Afiliasi
const HutangAfiliasiPage = lazy(() => import('@/modules/buku-pengawasan/hutang-afiliasi').then(m => ({ default: m.HutangAfiliasiPage })));

// Piutang Afiliasi
const PiutangAfiliasiPage = lazy(() => import('@/modules/buku-pengawasan/piutang-afiliasi').then(m => ({ default: m.PiutangAfiliasiPage })));

// Piutang Pihak Ketiga
const PiutangPihakKetigaPage = lazy(() => import('@/modules/buku-pengawasan/piutang-pihak-ketiga').then(m => ({ default: m.PiutangPihakKetigaPage })));

// Bank Garansi
const BankGaransiPage = lazy(() => import('@/modules/buku-pengawasan/bank-garansi').then(m => ({ default: m.BankGaransiPage })));

// Hutang-Piutang Dividen
const HutangDividenPage = lazy(() => import('@/modules/buku-pengawasan/hutang-dividen').then(m => ({ default: m.HutangDividenPage })));
const PiutangDividenPage = lazy(() => import('@/modules/buku-pengawasan/piutang-dividen').then(m => ({ default: m.PiutangDividenPage })));

// Gaji
const GajiPage = lazy(() => import('@/modules/buku-pengawasan/gaji').then(m => ({ default: m.GajiPage })));

// Turunan Gaji - 4 halaman terpisah
const HutangBpjsKesehatanPage = lazy(() => import('@/modules/buku-pengawasan/gaji/turunan/HutangBpjsKesehatanPage'));
const HutangBpjsKetenagakerjaanPage = lazy(() => import('@/modules/buku-pengawasan/gaji/turunan/HutangBpjsKetenagakerjaanPage'));
const HutangKoperasiKaryawanPage = lazy(() => import('@/modules/buku-pengawasan/gaji/turunan/HutangKoperasiKaryawanPage'));
const HutangSerikatPage = lazy(() => import('@/modules/buku-pengawasan/gaji/turunan/HutangSerikatPage'));

// Piutang Usaha - Multi-Tab Internal Pattern (1 page dengan 10 tab internal)
const PiutangUsahaPage = lazy(() => import('@/modules/buku-pengawasan/piutang-usaha').then(m => ({ default: m.PiutangUsahaPage })));

// Penjualan Termin - Multi-Tab Internal Pattern (8 tab mata uang). Tahap 1: read-only + Bar Saldo.
const PenjualanTerminPage = lazy(() => import('@/modules/buku-pengawasan/penjualan-termin').then(m => ({ default: m.PenjualanTerminPage })));

// Pembelian Termin - Multi-Tab Internal Pattern (8 tab mata uang). Mirror Penjualan Termin (ASET).
const PembelianTerminPage = lazy(() => import('@/modules/buku-pengawasan/pembelian-termin').then(m => ({ default: m.PembelianTerminPage })));

// Hutang Pajak - PPh Pasal 25
const HutangPPh25Page = lazy(() => import('@/modules/buku-pengawasan/hutang-pajak/05-pph-25').then(m => ({ default: m.HutangPPh25Page })));

// Hutang Pajak - PPh Pasal 21 (3-tab: Ringkasan / Kode 100 / Kode 401)
const HutangPPh21Page = lazy(() => import('@/modules/buku-pengawasan/hutang-pajak/01-pph-21').then(m => ({ default: m.HutangPPh21Page })));

// Data Awal - Hutang PPh Pasal 21 (reuse HutangPPh21Page dengan pageMode=DataAwal)
const DataAwalHutangPPh21Page = lazy(() =>
  import('@/modules/buku-pengawasan/hutang-pajak/01-pph-21').then(m => {
    const { HutangPPh21Page, PageMode_DataAwal } = m;
    const WrapperComponent = () => <HutangPPh21Page pageMode={PageMode_DataAwal} />;
    return { default: WrapperComponent };
  }),
);

// Hutang Pajak - PPh Pasal 23 (6-tab: Ringkasan + Kode 100/101/102/103/104)
const HutangPPh23Page = lazy(() => import('@/modules/buku-pengawasan/hutang-pajak/03-pph-23').then(m => ({ default: m.HutangPPh23Page })));

// Data Awal - Hutang PPh Pasal 23 (reuse HutangPPh23Page dengan pageMode=DataAwal)
const DataAwalHutangPPh23Page = lazy(() =>
  import('@/modules/buku-pengawasan/hutang-pajak/03-pph-23').then(m => {
    const { HutangPPh23Page, PageMode_DataAwal } = m;
    const WrapperComponent = () => <HutangPPh23Page pageMode={PageMode_DataAwal} />;
    return { default: WrapperComponent };
  }),
);

// Hutang Pajak - PPh Pasal 26 (7-tab: Ringkasan + Kode 100/101/102/103/104/105)
const HutangPPh26Page = lazy(() => import('@/modules/buku-pengawasan/hutang-pajak/06-pph-26').then(m => ({ default: m.HutangPPh26Page })));

// Data Awal - Hutang PPh Pasal 26 (reuse HutangPPh26Page dengan pageMode=DataAwal)
const DataAwalHutangPPh26Page = lazy(() =>
  import('@/modules/buku-pengawasan/hutang-pajak/06-pph-26').then(m => {
    const { HutangPPh26Page, PageMode_DataAwal } = m;
    const WrapperComponent = () => <HutangPPh26Page pageMode={PageMode_DataAwal} />;
    return { default: WrapperComponent };
  }),
);

// Hutang Pajak - PPh Pasal 4(2) FINAL (5-tab: Ringkasan + Kode 402/403/409/419)
const HutangPPh42Page = lazy(() => import('@/modules/buku-pengawasan/hutang-pajak/07-pph-42').then(m => ({ default: m.HutangPPh42Page })));

// Pelaporan PPN — single page, 30 kolom aggregate per bulan.
// Lihat .claude/docs/Booku v2/buku-pengawasan/pelaporan-ppn.md
const PelaporanPPNPage = lazy(() => import('@/modules/buku-pengawasan/hutang-pajak/10-ppn').then(m => ({ default: m.PelaporanPPNPage })));

// Ketetapan Pajak — Buku Pengawasan untuk SKP/STP dari DJP.
// Lihat .claude/docs/Booku v2/buku-pengawasan/pajak/ketetapan-pajak.md
const BukuPengawasanKetetapanPajakPage = lazy(() =>
  import('@/modules/buku-pengawasan/hutang-pajak/11-ketetapan').then((m) => ({
    default: m.BukuPengawasanKetetapanPajakPage,
  })),
);

// Bukti Potong PPh (Paid & Prepaid) — Multi-Tab Host (shared View + Hook, 2 Host terpisah)
const BuktiPotongPaidHost = lazy(() => import('@/modules/pajak/bukti-potong').then(m => ({ default: m.BuktiPotongPaidHost })));
const BuktiPotongPrepaidHost = lazy(() => import('@/modules/pajak/bukti-potong').then(m => ({ default: m.BuktiPotongPrepaidHost })));

// Data Awal - Hutang PPh Pasal 4(2) (reuse HutangPPh42Page dengan pageMode=DataAwal)
const DataAwalHutangPPh42Page = lazy(() =>
  import('@/modules/buku-pengawasan/hutang-pajak/07-pph-42').then(m => {
    const { HutangPPh42Page, PageMode_DataAwal } = m;
    const WrapperComponent = () => <HutangPPh42Page pageMode={PageMode_DataAwal} />;
    return { default: WrapperComponent };
  }),
);

// ============================================
// DATA AWAL - Wrapper Components (reuse page dengan pageMode=DataAwal)
// Halaman yang sama dengan Buku Pengawasan, tapi dengan mode DataAwal
// ============================================

// Data Awal - Hutang Usaha (reuse HutangUsahaPage)
const DataAwalHutangUsahaPage = lazy(() =>
  import('@/modules/buku-pengawasan/hutang-usaha').then(m => {
    const { HutangUsahaPage, PageMode_DataAwal } = m;
    const WrapperComponent = () => <HutangUsahaPage pageMode={PageMode_DataAwal} />;
    return { default: WrapperComponent };
  })
);

// Data Awal - Piutang Usaha (reuse PiutangUsahaPage)
const DataAwalPiutangUsahaPage = lazy(() =>
  import('@/modules/buku-pengawasan/piutang-usaha').then(m => {
    const { PiutangUsahaPage, PageMode_DataAwal } = m;
    const WrapperComponent = () => <PiutangUsahaPage pageMode={PageMode_DataAwal} />;
    return { default: WrapperComponent };
  })
);

// Data Awal - Penjualan Termin (reuse PenjualanTerminPage)
const DataAwalPenjualanTerminPage = lazy(() =>
  import('@/modules/buku-pengawasan/penjualan-termin').then(m => {
    const { PenjualanTerminPage, PageMode_DataAwal } = m;
    const WrapperComponent = () => <PenjualanTerminPage pageMode={PageMode_DataAwal} />;
    return { default: WrapperComponent };
  })
);

// Data Awal - Pembelian Termin (reuse PembelianTerminPage)
const DataAwalPembelianTerminPage = lazy(() =>
  import('@/modules/buku-pengawasan/pembelian-termin').then(m => {
    const { PembelianTerminPage, PageMode_DataAwal } = m;
    const WrapperComponent = () => <PembelianTerminPage pageMode={PageMode_DataAwal} />;
    return { default: WrapperComponent };
  })
);

// Data Awal - Hutang Pihak Ketiga (reuse HutangPihakKetigaPage)
const DataAwalHutangPihakKetigaPage = lazy(() =>
  import('@/modules/buku-pengawasan/hutang-pihak-ketiga').then(m => {
    const { HutangPihakKetigaPage, PageMode_DataAwal } = m;
    const WrapperComponent = () => <HutangPihakKetigaPage pageMode={PageMode_DataAwal} />;
    return { default: WrapperComponent };
  })
);

// Data Awal - Piutang Pihak Ketiga (reuse PiutangPihakKetigaPage)
const DataAwalPiutangPihakKetigaPage = lazy(() =>
  import('@/modules/buku-pengawasan/piutang-pihak-ketiga').then(m => {
    const { PiutangPihakKetigaPage, PageMode_DataAwal } = m;
    const WrapperComponent = () => <PiutangPihakKetigaPage pageMode={PageMode_DataAwal} />;
    return { default: WrapperComponent };
  })
);

// Data Awal - Hutang Afiliasi (reuse HutangAfiliasiPage)
const DataAwalHutangAfiliasiPage = lazy(() =>
  import('@/modules/buku-pengawasan/hutang-afiliasi').then(m => {
    const { HutangAfiliasiPage, PageMode_DataAwal } = m;
    const WrapperComponent = () => <HutangAfiliasiPage pageMode={PageMode_DataAwal} />;
    return { default: WrapperComponent };
  })
);

// Data Awal - Piutang Afiliasi (reuse PiutangAfiliasiPage)
const DataAwalPiutangAfiliasiPage = lazy(() =>
  import('@/modules/buku-pengawasan/piutang-afiliasi').then(m => {
    const { PiutangAfiliasiPage, PageMode_DataAwal } = m;
    const WrapperComponent = () => <PiutangAfiliasiPage pageMode={PageMode_DataAwal} />;
    return { default: WrapperComponent };
  })
);

// Data Awal - Hutang Karyawan (reuse HutangKaryawanPage)
const DataAwalHutangKaryawanPage = lazy(() =>
  import('@/modules/buku-pengawasan/hutang-piutang-karyawan/hutang').then(m => {
    const { HutangKaryawanPage, PageMode_DataAwal } = m;
    const WrapperComponent = () => <HutangKaryawanPage pageMode={PageMode_DataAwal} />;
    return { default: WrapperComponent };
  })
);

// Data Awal - Piutang Karyawan (reuse PiutangKaryawanPage)
const DataAwalPiutangKaryawanPage = lazy(() =>
  import('@/modules/buku-pengawasan/hutang-piutang-karyawan/piutang').then(m => {
    const { PiutangKaryawanPage, PageMode_DataAwal } = m;
    const WrapperComponent = () => <PiutangKaryawanPage pageMode={PageMode_DataAwal} />;
    return { default: WrapperComponent };
  })
);

// Data Awal - Hutang Pemegang Saham (reuse HutangPemegangSahamPage)
const DataAwalHutangPemegangSahamPage = lazy(() =>
  import('@/modules/buku-pengawasan/hutang-piutang-pemegang-saham/hutang').then(m => {
    const { HutangPemegangSahamPage, PageMode_DataAwal } = m;
    const WrapperComponent = () => <HutangPemegangSahamPage pageMode={PageMode_DataAwal} />;
    return { default: WrapperComponent };
  })
);

// Data Awal - Piutang Pemegang Saham (reuse PiutangPemegangSahamPage)
const DataAwalPiutangPemegangSahamPage = lazy(() =>
  import('@/modules/buku-pengawasan/hutang-piutang-pemegang-saham/piutang').then(m => {
    const { PiutangPemegangSahamPage, PageMode_DataAwal } = m;
    const WrapperComponent = () => <PiutangPemegangSahamPage pageMode={PageMode_DataAwal} />;
    return { default: WrapperComponent };
  })
);

// Data Awal - Deposit Operasional (reuse DepositOperasionalPage dengan pageMode=DataAwal)
const DataAwalDepositOperasionalPage = lazy(() =>
  import('@/modules/buku-pengawasan/deposit-operasional').then(m => {
    const { DepositOperasionalPage, PageMode_DataAwal } = m;
    const WrapperComponent = () => <DepositOperasionalPage pageMode={PageMode_DataAwal} />;
    return { default: WrapperComponent };
  })
);

// Data Awal - Hutang Gaji (reuse GajiPage dengan pageMode=DataAwal)
const DataAwalGajiPage = lazy(() =>
  import('@/modules/buku-pengawasan/gaji').then(m => {
    const { GajiPage, PageMode_DataAwal } = m;
    const WrapperComponent = () => <GajiPage pageMode={PageMode_DataAwal} />;
    return { default: WrapperComponent };
  })
);

// Data Awal - Turunan Gaji (4 varian, reuse TurunanGajiPage dgn pageMode=DataAwal + config per varian)
const DataAwalTurunanGajiBpjsKesehatanPage = lazy(() =>
  import('@/modules/buku-pengawasan/gaji/turunan').then(m => {
    const { TurunanGajiPage, PageMode_DataAwal, CONFIG_BPJS_KESEHATAN } = m;
    const WrapperComponent = () => (
      <TurunanGajiPage config={CONFIG_BPJS_KESEHATAN} pageMode={PageMode_DataAwal} />
    );
    return { default: WrapperComponent };
  })
);

const DataAwalTurunanGajiBpjsKetenagakerjaanPage = lazy(() =>
  import('@/modules/buku-pengawasan/gaji/turunan').then(m => {
    const { TurunanGajiPage, PageMode_DataAwal, CONFIG_BPJS_KETENAGAKERJAAN } = m;
    const WrapperComponent = () => (
      <TurunanGajiPage config={CONFIG_BPJS_KETENAGAKERJAAN} pageMode={PageMode_DataAwal} />
    );
    return { default: WrapperComponent };
  })
);

const DataAwalTurunanGajiKoperasiPage = lazy(() =>
  import('@/modules/buku-pengawasan/gaji/turunan').then(m => {
    const { TurunanGajiPage, PageMode_DataAwal, CONFIG_KOPERASI } = m;
    const WrapperComponent = () => (
      <TurunanGajiPage config={CONFIG_KOPERASI} pageMode={PageMode_DataAwal} />
    );
    return { default: WrapperComponent };
  })
);

const DataAwalTurunanGajiSerikatPage = lazy(() =>
  import('@/modules/buku-pengawasan/gaji/turunan').then(m => {
    const { TurunanGajiPage, PageMode_DataAwal, CONFIG_SERIKAT } = m;
    const WrapperComponent = () => (
      <TurunanGajiPage config={CONFIG_SERIKAT} pageMode={PageMode_DataAwal} />
    );
    return { default: WrapperComponent };
  })
);

// Manajemen Asset
const DaftarPenyusutanAssetPage = lazy(() => import('@/modules/manajemen-asset/daftar-penyusutan-asset').then(m => ({ default: m.DaftarPenyusutanAssetPage })));
const DaftarAmortisasiBiayaPage = lazy(() => import('@/modules/manajemen-asset/daftar-amortisasi-biaya').then(m => ({ default: m.DaftarAmortisasiBiayaPage })));

// Stock Opname — dual-route wrapper pattern (NORMAL + Data Awal LAMPAU)
const StockOpnamePage = lazy(() => import('@/modules/stock-opname').then(m => ({ default: m.StockOpnamePage })));
const DataAwalStockOpnamePage = lazy(() =>
  import('@/modules/stock-opname').then(m => {
    const { StockOpnamePage, PageMode_DataAwal } = m;
    const WrapperComponent = () => <StockOpnamePage pageMode={PageMode_DataAwal} />;
    return { default: WrapperComponent };
  })
);

// ============================================
// TYPES
// ============================================

export interface PageDefinition {
  id: string;
  path: string;
  label: string;
  icon: ReactNode;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  component: LazyExoticComponent<ComponentType<any>>;
  minLevel?: number;
  requiresDagang?: boolean;
  requiresManufaktur?: boolean;
  requiresJasa?: boolean;
  requiresTahunBukuNormal?: boolean;
  requiresTahunBukuLampau?: boolean;
}

// ============================================
// PAGE REGISTRY
// ============================================

export const pageRegistry: PageDefinition[] = [
  // Beranda
  {
    id: 'beranda',
    path: '/',
    label: 'Beranda',
    icon: <Home size={16} />,
    component: DashboardPage,
  },

  // Data Master
  {
    id: 'data-user',
    path: '/users',
    label: 'Data User',
    icon: <Users size={16} />,
    component: UserManagementPage,
    minLevel: LevelUser_09_SuperUser,
  },
  {
    id: 'data-coa',
    path: '/data/coa',
    label: 'Data COA',
    icon: <BookOpen size={16} />,
    component: CoaManagementPage,
    minLevel: LevelUser_03_Direktur,
  },
  {
    id: 'data-lawan-transaksi',
    path: '/data/lawan-transaksi',
    label: 'Lawan Transaksi',
    icon: <Handshake size={16} />,
    component: LawanTransaksiManagementPage,
    minLevel: LevelUser_03_Direktur,
  },
  {
    id: 'data-toko',
    path: '/data/toko',
    label: 'Data Toko',
    icon: <Building2 size={16} />,
    component: TokoManagementPage,
    requiresDagang: true,
  },
  {
    id: 'data-karyawan',
    path: '/data/karyawan',
    label: 'Data Karyawan',
    icon: <UserSquare2 size={16} />,
    component: KaryawanManagementPage,
    minLevel: LevelUser_03_Direktur,
  },
  {
    id: 'data-pemegang-saham',
    path: '/data/pemegang-saham',
    label: 'Pemegang Saham',
    icon: <Briefcase size={16} />,
    component: PemegangSahamPage,
    minLevel: LevelUser_03_Direktur,
  },
  {
    id: 'data-project',
    path: '/data/project',
    label: 'Data Project',
    icon: <ClipboardList size={16} />,
    component: DataProjectManagementPage,
    minLevel: LevelUser_03_Direktur,
  },

  // ============================================
  // DATA AWAL
  // Menu ini HANYA muncul di Tahun Buku Lampau
  // Menggunakan halaman yang sama dengan Buku Pengawasan (reusable)
  // ============================================

  // ─────────── Validasi Saldo Awal (item #1 di submenu Data Awal) ───────────
  // Lihat .claude/docs/Booku v2/buku-pengawasan/validasi-saldo-awal.md
  { id: 'data-awal-validasi-saldo-awal', path: '/data-awal/validasi-saldo-awal', label: 'Validasi Saldo Awal', icon: <FileText size={16} />, component: DataAwalValidasiSaldoAwalPage, minLevel: LevelUser_03_Direktur, requiresTahunBukuLampau: true },

  // ─────────── Hutang Usaha ───────────
  // Reuse HutangUsahaPage dengan pageMode=DataAwal
  { id: 'data-awal-hutang-usaha', path: '/data-awal/hutang/usaha', label: 'Saldo Akhir Hutang Usaha', icon: <Receipt size={16} />, component: DataAwalHutangUsahaPage, requiresTahunBukuLampau: true },

  // ─────────── Hutang Lainnya ───────────
  // Reuse HutangBankPage/HutangLeasingPage. Komponen otomatis render mode LAMPAU
  // (judul "Saldo Akhir Hutang ...", sembunyikan Posting Pencairan, dll) via JenisTahunBuku.
  { id: 'data-awal-hutang-bank', path: '/data-awal/hutang/bank', label: 'Hutang Bank', icon: <Landmark size={16} />, component: HutangBankPage, requiresTahunBukuLampau: true },
  { id: 'data-awal-hutang-leasing', path: '/data-awal/hutang/leasing', label: 'Hutang Leasing', icon: <CreditCard size={16} />, component: HutangLeasingPage, requiresTahunBukuLampau: true },
  { id: 'data-awal-hutang-pihak-ketiga', path: '/data-awal/hutang/pihak-ketiga', label: 'Saldo Akhir Hutang Pihak Ketiga', icon: <Handshake size={16} />, component: DataAwalHutangPihakKetigaPage, requiresTahunBukuLampau: true },
  { id: 'data-awal-hutang-afiliasi', path: '/data-awal/hutang/afiliasi', label: 'Saldo Akhir Hutang Afiliasi', icon: <Building2 size={16} />, component: DataAwalHutangAfiliasiPage, requiresTahunBukuLampau: true },
  { id: 'data-awal-hutang-karyawan', path: '/data-awal/hutang/karyawan', label: 'Saldo Akhir Hutang Karyawan', icon: <UserSquare2 size={16} />, component: DataAwalHutangKaryawanPage, requiresTahunBukuLampau: true },
  { id: 'data-awal-hutang-pemegang-saham', path: '/data-awal/hutang/pemegang-saham', label: 'Saldo Akhir Hutang Pemegang Saham', icon: <Users size={16} />, component: DataAwalHutangPemegangSahamPage, requiresTahunBukuLampau: true },

  // ─────────── Hutang Pajak ───────────
  { id: 'data-awal-hutang-pph-21', path: '/data-awal/hutang/pajak/pph-21', label: 'Hutang PPh 21', icon: <Calculator size={16} />, component: DataAwalHutangPPh21Page, minLevel: LevelUser_03_Direktur, requiresTahunBukuLampau: true },
  { id: 'data-awal-hutang-pph-23', path: '/data-awal/hutang/pajak/pph-23', label: 'Hutang PPh 23', icon: <Calculator size={16} />, component: DataAwalHutangPPh23Page, minLevel: LevelUser_03_Direktur, requiresTahunBukuLampau: true },
  { id: 'data-awal-hutang-pph-42', path: '/data-awal/hutang/pajak/pph-42', label: 'Hutang PPh 4(2)', icon: <Calculator size={16} />, component: DataAwalHutangPPh42Page, minLevel: LevelUser_03_Direktur, requiresTahunBukuLampau: true },
  // Data Awal: Hutang PPh 25 — pakai halaman yang sama (HutangPPh25Page).
  // Perilaku LAMPAU sudah di-handle via useAppStore().JenisTahunBuku di halaman.
  { id: 'data-awal-hutang-pph-25', path: '/data-awal/hutang/pajak/pph-25', label: 'Hutang PPh 25', icon: <Calculator size={16} />, component: HutangPPh25Page, minLevel: LevelUser_03_Direktur, requiresTahunBukuLampau: true },
  { id: 'data-awal-hutang-pph-26', path: '/data-awal/hutang/pajak/pph-26', label: 'Hutang PPh 26', icon: <Calculator size={16} />, component: DataAwalHutangPPh26Page, minLevel: LevelUser_03_Direktur, requiresTahunBukuLampau: true },
  { id: 'data-awal-hutang-pph-29', path: '/data-awal/hutang/pajak/pph-29', label: 'Hutang PPh 29', icon: <Calculator size={16} />, component: lazy(() => Promise.resolve({ default: () => <PlaceholderContent title="Data Awal: Hutang PPh Pasal 29" /> })), requiresTahunBukuLampau: true },
  { id: 'data-awal-hutang-ppn', path: '/data-awal/hutang/pajak/ppn', label: 'Hutang PPN', icon: <Calculator size={16} />, component: PelaporanPPNPage, minLevel: LevelUser_03_Direktur, requiresTahunBukuLampau: true },
  { id: 'data-awal-hutang-ketetapan-pajak', path: '/data-awal/hutang/pajak/ketetapan', label: 'Hutang Ketetapan Pajak', icon: <Calculator size={16} />, component: BukuPengawasanKetetapanPajakPage, minLevel: LevelUser_03_Direktur, requiresTahunBukuLampau: true },

  // ─────────── Gaji ───────────
  { id: 'data-awal-gaji-gaji', path: '/data-awal/hutang/gaji/gaji', label: 'Saldo Akhir Hutang Gaji', icon: <Banknote size={16} />, component: DataAwalGajiPage, requiresTahunBukuLampau: true },
  { id: 'data-awal-gaji-bpjs-kesehatan', path: '/data-awal/hutang/gaji/bpjs-kesehatan', label: 'Saldo Akhir Hutang BPJS Kesehatan', icon: <Banknote size={16} />, component: DataAwalTurunanGajiBpjsKesehatanPage, requiresTahunBukuLampau: true },
  { id: 'data-awal-gaji-bpjs-ketenagakerjaan', path: '/data-awal/hutang/gaji/bpjs-ketenagakerjaan', label: 'Saldo Akhir Hutang BPJS Ketenagakerjaan', icon: <Banknote size={16} />, component: DataAwalTurunanGajiBpjsKetenagakerjaanPage, requiresTahunBukuLampau: true },
  { id: 'data-awal-gaji-koperasi', path: '/data-awal/hutang/gaji/koperasi', label: 'Saldo Akhir Hutang Koperasi Karyawan', icon: <Banknote size={16} />, component: DataAwalTurunanGajiKoperasiPage, requiresTahunBukuLampau: true },
  { id: 'data-awal-gaji-serikat', path: '/data-awal/hutang/gaji/serikat', label: 'Saldo Akhir Hutang Serikat', icon: <Banknote size={16} />, component: DataAwalTurunanGajiSerikatPage, requiresTahunBukuLampau: true },

  // ─────────── Piutang Usaha ───────────
  // Reuse PiutangUsahaPage dengan pageMode=DataAwal
  { id: 'data-awal-piutang-usaha', path: '/data-awal/piutang/usaha', label: 'Saldo Akhir Piutang Usaha', icon: <Receipt size={16} />, component: DataAwalPiutangUsahaPage, requiresTahunBukuLampau: true },
  // ─────────── Penjualan Termin ───────────
  // Reuse PenjualanTerminPage dengan pageMode=DataAwal (Tambah PO + Tambah Invoice Termin per PO)
  { id: 'data-awal-penjualan-termin', path: '/data-awal/termin/penjualan', label: 'Saldo Akhir Penjualan Termin', icon: <Receipt size={16} />, component: DataAwalPenjualanTerminPage, requiresTahunBukuLampau: true },
  // ─────────── Pembelian Termin ───────────
  // Reuse PembelianTerminPage dengan pageMode=DataAwal (Tambah PO + Tambah Invoice Termin per PO)
  { id: 'data-awal-pembelian-termin', path: '/data-awal/termin/pembelian', label: 'Saldo Akhir Pembelian Termin', icon: <Receipt size={16} />, component: DataAwalPembelianTerminPage, requiresTahunBukuLampau: true },

  // ─────────── Piutang Lainnya ───────────
  { id: 'data-awal-piutang-pihak-ketiga', path: '/data-awal/piutang/pihak-ketiga', label: 'Saldo Akhir Piutang Pihak Ketiga', icon: <Handshake size={16} />, component: DataAwalPiutangPihakKetigaPage, requiresTahunBukuLampau: true },
  { id: 'data-awal-piutang-afiliasi', path: '/data-awal/piutang/afiliasi', label: 'Saldo Akhir Piutang Afiliasi', icon: <Building2 size={16} />, component: DataAwalPiutangAfiliasiPage, requiresTahunBukuLampau: true },
  { id: 'data-awal-piutang-karyawan', path: '/data-awal/piutang/karyawan', label: 'Saldo Akhir Piutang Karyawan', icon: <UserSquare2 size={16} />, component: DataAwalPiutangKaryawanPage, requiresTahunBukuLampau: true },
  { id: 'data-awal-piutang-pemegang-saham', path: '/data-awal/piutang/pemegang-saham', label: 'Saldo Akhir Piutang Pemegang Saham', icon: <Users size={16} />, component: DataAwalPiutangPemegangSahamPage, requiresTahunBukuLampau: true },
  { id: 'data-awal-deposit-operasional', path: '/data-awal/piutang/deposit-operasional', label: 'Saldo Akhir Deposit Operasional', icon: <Wallet size={16} />, component: DataAwalDepositOperasionalPage, requiresTahunBukuLampau: true },
  // Bank Garansi (COA 115310001) — reuse BankGaransiPage; mode LAMPAU auto-deteksi via JenisTahunBuku.
  { id: 'data-awal-bank-garansi', path: '/data-awal/piutang/bank-garansi', label: 'Saldo Akhir Bank Garansi', icon: <Landmark size={16} />, component: BankGaransiPage, minLevel: LevelUser_01_Operator, requiresTahunBukuLampau: true },

  // ─────────── Persediaan (Stock Opname) ───────────
  // Hanya tampil untuk perusahaan manufaktur. Komponen yang sama dengan NORMAL,
  // mode dibedakan via prop pageMode (lewat wrapper DataAwalStockOpnamePage).
  { id: 'data-awal-stock-opname', path: '/data-awal/stock-opname', label: 'Saldo Akhir Stock Opname', icon: <Package size={16} />, component: DataAwalStockOpnamePage, requiresTahunBukuLampau: true, requiresManufaktur: true },

  // ─────────── Asset ───────────
  { id: 'data-awal-amortisasi-biaya', path: '/data-awal/asset/amortisasi-biaya', label: 'Amortisasi Biaya', icon: <PieChart size={16} />, component: DaftarAmortisasiBiayaPage, requiresTahunBukuLampau: true },
  { id: 'data-awal-asset-tetap', path: '/data-awal/asset/asset-tetap', label: 'Asset Tetap', icon: <Package size={16} />, component: DaftarPenyusutanAssetPage, requiresTahunBukuLampau: true },

  // Jurnal
  {
    id: 'jurnal-umum',
    path: '/jurnal/umum',
    label: 'Jurnal Umum',
    icon: <FileText size={16} />,
    component: JurnalUmumPage,
    minLevel: LevelUser_03_Direktur,
    requiresTahunBukuNormal: true,
  },
  // Adjustment Akhir Bulan — konsolidasi 4 jenis adjustment dalam 1 halaman.
  // Menggantikan 4 placeholder lama (jurnal/adjusment/{penyusutan,amortisasi,forex,hpp}).
  // Hanya tersedia di Tahun Buku NORMAL.
  {
    id: 'adjustment-akhir-bulan',
    path: '/jurnal/adjustment-akhir-bulan',
    label: 'Adjustment Akhir Bulan',
    icon: <FileText size={16} />,
    component: AdjustmentAkhirBulanPage,
    minLevel: LevelUser_03_Direktur,
    requiresTahunBukuNormal: true,
  },

  // Pembelian
  // PO Pembelian - MDM pattern (8 mode dalam 1 halaman)
  {
    id: 'po-pembelian',
    path: '/pembelian/po',
    label: 'PO Pembelian',
    icon: <ClipboardList size={16} />,
    component: POPembelianPage,
    minLevel: LevelUser_03_Direktur,
    requiresTahunBukuNormal: true,
  },
  // Surat Jalan - Single page
  {
    id: 'surat-jalan-pembelian',
    path: '/pembelian/surat-jalan',
    label: 'Surat Jalan',
    icon: <FileText size={16} />,
    component: SJPembelianPage,
    minLevel: LevelUser_03_Direktur,
    requiresTahunBukuNormal: true,
  },
  // BAST - Single page
  {
    id: 'bast-pembelian',
    path: '/pembelian/bast',
    label: 'BAST',
    icon: <FileText size={16} />,
    component: BASTPembelianPage,
    minLevel: LevelUser_03_Direktur,
    requiresTahunBukuNormal: true,
  },
  // Invoice Pembelian - MDM pattern (11 mode dalam 1 halaman)
  {
    id: 'invoice-pembelian',
    path: '/pembelian/invoice',
    label: 'Invoice Pembelian',
    icon: <Receipt size={16} />,
    component: lazy(() => import('@/modules/pembelian-penjualan/pembelian/invoice/InvoicePembelianPage')),
    minLevel: LevelUser_03_Direktur,
    requiresTahunBukuNormal: true,
  },
  // Buku Pembelian - Single-Tab Host (1 tab aplikasi, tab internal: Lokal/Impor)
  {
    id: 'buku-pembelian',
    path: '/pembelian/buku',
    label: 'Buku Pembelian',
    icon: <BookOpen size={16} />,
    component: lazy(() => import('@/modules/pembelian-penjualan/pembelian/buku/BukuPembelianPage')),
    minLevel: LevelUser_03_Direktur,
    requiresTahunBukuNormal: true,
  },
  // Retur Pembelian - Single page
  {
    id: 'retur-pembelian',
    path: '/pembelian/retur',
    label: 'Retur Pembelian',
    icon: <Receipt size={16} />,
    component: lazy(() => Promise.resolve({ default: () => <PlaceholderContent title="Retur Pembelian" /> })),
    minLevel: LevelUser_03_Direktur,
    requiresTahunBukuNormal: true,
  },

  // ============================================
  // PENJUALAN
  // ============================================

  // PO Penjualan - MDM Pattern (mode dalam 1 halaman: Lokal × Jenis Produk, Ekspor × Barang/Semua)
  {
    id: 'po-penjualan',
    path: '/penjualan/po',
    label: 'PO Penjualan',
    icon: <ClipboardList size={16} />,
    component: POPenjualanPage,
    minLevel: LevelUser_03_Direktur,
    requiresTahunBukuNormal: true,
  },
  // Surat Jalan Penjualan - Single page
  {
    id: 'surat-jalan-penjualan',
    path: '/penjualan/surat-jalan',
    label: 'Surat Jalan',
    icon: <FileText size={16} />,
    component: SJPenjualanPage,
    minLevel: LevelUser_03_Direktur,
    requiresTahunBukuNormal: true,
  },
  // BAST Penjualan - Single page
  {
    id: 'bast-penjualan',
    path: '/penjualan/bast',
    label: 'BAST',
    icon: <FileText size={16} />,
    component: BASTPenjualanPage,
    minLevel: LevelUser_03_Direktur,
    requiresTahunBukuNormal: true,
  },
  // Invoice Penjualan - MDM pattern (10 mode dalam 1 halaman)
  {
    id: 'invoice-penjualan',
    path: '/penjualan/invoice',
    label: 'Invoice Penjualan',
    icon: <Receipt size={16} />,
    component: lazy(() => import('@/modules/pembelian-penjualan/penjualan/invoice/InvoicePenjualanPage')),
    minLevel: LevelUser_03_Direktur,
    requiresTahunBukuNormal: true,
  },
  // Buku Penjualan - Single-Tab Host (1 tab aplikasi, tab internal: Lokal/Ekspor)
  {
    id: 'buku-penjualan',
    path: '/penjualan/buku',
    label: 'Buku Penjualan',
    icon: <BookOpen size={16} />,
    component: lazy(() => import('@/modules/pembelian-penjualan/penjualan/buku/BukuPenjualanPage')),
    minLevel: LevelUser_03_Direktur,
    requiresTahunBukuNormal: true,
  },
  // Penjualan Eceran - Multi-Tab Host (tiap rekap = tab aplikasi terpisah)
  {
    id: 'penjualan-eceran-harian',
    path: '/penjualan/eceran/harian',
    label: 'Penjualan Eceran - Rekap Harian',
    icon: <Calendar size={16} />,
    component: lazy(() => Promise.resolve({ default: () => <PlaceholderContent title="Penjualan Eceran - Rekap Harian" /> })),
    minLevel: LevelUser_03_Direktur,
    requiresTahunBukuNormal: true,
  },
  {
    id: 'penjualan-eceran-bulanan',
    path: '/penjualan/eceran/bulanan',
    label: 'Penjualan Eceran - Rekap Bulanan',
    icon: <Calendar size={16} />,
    component: lazy(() => Promise.resolve({ default: () => <PlaceholderContent title="Penjualan Eceran - Rekap Bulanan" /> })),
    minLevel: LevelUser_03_Direktur,
    requiresTahunBukuNormal: true,
  },
  {
    id: 'penjualan-eceran-tahunan',
    path: '/penjualan/eceran/tahunan',
    label: 'Penjualan Eceran - Rekap Tahunan',
    icon: <Calendar size={16} />,
    component: lazy(() => Promise.resolve({ default: () => <PlaceholderContent title="Penjualan Eceran - Rekap Tahunan" /> })),
    minLevel: LevelUser_03_Direktur,
    requiresTahunBukuNormal: true,
  },
  // Retur Penjualan - Single page
  {
    id: 'retur-penjualan',
    path: '/penjualan/retur',
    label: 'Retur Penjualan',
    icon: <Receipt size={16} />,
    component: lazy(() => Promise.resolve({ default: () => <PlaceholderContent title="Retur Penjualan" /> })),
    minLevel: LevelUser_03_Direktur,
    requiresTahunBukuNormal: true,
  },

  // ============================================
  // TRANSAKSI
  // ============================================
  {
    id: 'transaksi-in',
    path: '/transaksi/in',
    label: 'Transaksi IN',
    icon: <Receipt size={16} />,
    component: lazy(() => Promise.resolve({ default: () => <PlaceholderContent title="Transaksi IN" /> })),
    requiresTahunBukuNormal: true,
  },
  {
    id: 'transaksi-out',
    path: '/transaksi/out',
    label: 'Transaksi OUT',
    icon: <Receipt size={16} />,
    component: lazy(() => Promise.resolve({ default: () => <PlaceholderContent title="Transaksi OUT" /> })),
    requiresTahunBukuNormal: true,
  },

  // ============================================
  // STOCK OPNAME
  // Single-Tab Host: 5 mode via tab internal
  // Mode: Bahan Penolong, Bahan Baku, Barang Jadi, Barang Dalam Proses (Cek Fisik), Barang Dalam Proses (Tarikan Data)
  // ============================================
  {
    id: 'stock-opname',
    path: '/stock-opname',
    label: 'Stock Opname',
    icon: <Package size={16} />,
    component: StockOpnamePage,
    requiresTahunBukuNormal: true,
    requiresManufaktur: true,
  },

  // ============================================
  // BUKU PENGAWASAN
  // ============================================

  // ─────────── Validasi Saldo Awal (item #1 di submenu Buku Pengawasan) ───────────
  // Lihat .claude/docs/Booku v2/buku-pengawasan/validasi-saldo-awal.md
  {
    id: 'validasi-saldo-awal',
    path: '/buku-pengawasan/validasi-saldo-awal',
    label: 'Validasi Saldo Awal',
    icon: <FileText size={16} />,
    component: ValidasiSaldoAwalPage,
    minLevel: LevelUser_03_Direktur,
    requiresTahunBukuNormal: true,
  },
  // ─────────── Validasi Saldo Akhir (item #2 — penghalang Tutup Buku Tahunan NORMAL) ───────────
  // Lihat .claude/docs/Booku v2/buku-pengawasan/validasi-saldo-akhir.md
  {
    id: 'validasi-saldo-akhir',
    path: ValidasiSaldoAkhir_Path,
    label: 'Validasi Saldo Akhir',
    icon: <FileText size={16} />,
    component: ValidasiSaldoAkhirPage,
    minLevel: LevelUser_03_Direktur,
    requiresTahunBukuNormal: true,
  },

  // ─────────── Bank & Cash ───────────
  {
    id: 'bank-garansi',
    path: '/buku-pengawasan/bank-garansi',
    label: 'Bank Garansi',
    icon: <Landmark size={16} />,
    component: BankGaransiPage,
    minLevel: LevelUser_01_Operator,
    requiresTahunBukuNormal: true,
  },

  // ─────────── Penerimaan-Pengeluaran ───────────
  {
    id: 'bukti-penerimaan',
    path: '/buku-pengawasan/bukti-penerimaan',
    label: 'Bukti Penerimaan Bank/Cash',
    icon: <ArrowDownCircle size={16} />,
    component: BuktiPenerimaanPage,
    minLevel: LevelUser_03_Direktur,
    requiresTahunBukuNormal: true,
  },
  {
    id: 'bukti-pengeluaran',
    path: '/buku-pengawasan/bukti-pengeluaran',
    label: 'Bukti Pengeluaran Bank/Cash',
    icon: <ArrowUpCircle size={16} />,
    component: BuktiPengeluaranPage,
    minLevel: LevelUser_03_Direktur,
    requiresTahunBukuNormal: true,
  },
  {
    id: 'bundel-pengajuan',
    path: '/buku-pengawasan/bundel-pengajuan',
    label: 'Bundel Pengajuan Pengeluaran',
    icon: <ClipboardList size={16} />,
    component: lazy(() => Promise.resolve({ default: () => <PlaceholderContent title="Bundel Pengajuan Pengeluaran" /> })),
    requiresTahunBukuNormal: true,
  },
  {
    id: 'pemindahbukuan',
    path: '/buku-pengawasan/pemindahbukuan',
    label: 'Pemindahbukuan',
    icon: <ArrowLeftRight size={16} />,
    component: PemindahbukuanPage,
    minLevel: LevelUser_03_Direktur,
    requiresTahunBukuNormal: true,
  },

  // ─────────── Gaji ───────────
  {
    id: 'buku-gaji',
    path: '/buku-pengawasan/gaji',
    label: 'Buku Gaji',
    icon: <Users size={16} />,
    component: GajiPage,
    minLevel: LevelUser_03_Direktur,
    requiresTahunBukuNormal: true,
  },
  // Turunan Gaji - 4 halaman terpisah di level aplikasi
  {
    id: 'hutang-bpjs-kesehatan',
    path: '/buku-pengawasan/hutang-bpjs-kesehatan',
    label: 'Hutang BPJS Kesehatan',
    icon: <Users size={16} />,
    component: HutangBpjsKesehatanPage,
    minLevel: LevelUser_03_Direktur,
    requiresTahunBukuNormal: true,
  },
  {
    id: 'hutang-bpjs-ketenagakerjaan',
    path: '/buku-pengawasan/hutang-bpjs-ketenagakerjaan',
    label: 'Hutang BPJS Ketenagakerjaan',
    icon: <Users size={16} />,
    component: HutangBpjsKetenagakerjaanPage,
    minLevel: LevelUser_03_Direktur,
    requiresTahunBukuNormal: true,
  },
  {
    id: 'hutang-koperasi-karyawan',
    path: '/buku-pengawasan/hutang-koperasi-karyawan',
    label: 'Hutang Koperasi Karyawan',
    icon: <Users size={16} />,
    component: HutangKoperasiKaryawanPage,
    minLevel: LevelUser_03_Direktur,
    requiresTahunBukuNormal: true,
  },
  {
    id: 'hutang-serikat',
    path: '/buku-pengawasan/hutang-serikat',
    label: 'Hutang Serikat',
    icon: <Users size={16} />,
    component: HutangSerikatPage,
    minLevel: LevelUser_03_Direktur,
    requiresTahunBukuNormal: true,
  },

  // ─────────── Hutang ───────────
  // Hutang Usaha - Multi-Tab Internal Pattern (1 page, 10 tab internal)
  {
    id: 'hutang-usaha',
    path: '/buku-pengawasan/hutang-usaha',
    label: 'Hutang Usaha',
    icon: <Receipt size={16} />,
    component: HutangUsahaPage,
    minLevel: LevelUser_03_Direktur,
    requiresTahunBukuNormal: true,
  },
  // Hutang Bank - Separate tab (Multi-Tab Host Pattern)
  {
    id: 'hutang-bank',
    path: '/buku-pengawasan/hutang-bank',
    label: 'Hutang Bank',
    icon: <Landmark size={16} />,
    component: HutangBankPage,
    minLevel: LevelUser_03_Direktur,
    requiresTahunBukuNormal: true,
  },
  // Hutang Leasing - Separate tab (Multi-Tab Host Pattern)
  {
    id: 'hutang-leasing',
    path: '/buku-pengawasan/hutang-leasing',
    label: 'Hutang Leasing',
    icon: <CreditCard size={16} />,
    component: HutangLeasingPage,
    minLevel: LevelUser_03_Direktur,
    requiresTahunBukuNormal: true,
  },
  // Hutang Pihak Ketiga
  {
    id: 'hutang-pihak-ketiga',
    path: '/buku-pengawasan/hutang-pihak-ketiga',
    label: 'Hutang Pihak Ketiga',
    icon: <Handshake size={16} />,
    component: HutangPihakKetigaPage,
    minLevel: LevelUser_03_Direktur,
  },
  // Hutang Afiliasi
  {
    id: 'hutang-afiliasi',
    path: '/buku-pengawasan/hutang-afiliasi',
    label: 'Hutang Afiliasi',
    icon: <Building2 size={16} />,
    component: HutangAfiliasiPage,
    minLevel: LevelUser_03_Direktur,
  },
  {
    id: 'hutang-karyawan',
    path: '/buku-pengawasan/hutang-karyawan',
    label: 'Hutang Karyawan',
    icon: <UserSquare2 size={16} />,
    component: HutangKaryawanPage,
    minLevel: LevelUser_03_Direktur,
    requiresTahunBukuNormal: true,
  },
  {
    id: 'hutang-pemegang-saham',
    path: '/buku-pengawasan/hutang-pemegang-saham',
    label: 'Hutang Pemegang Saham',
    icon: <Briefcase size={16} />,
    component: HutangPemegangSahamPage,
    minLevel: LevelUser_03_Direktur,
  },
  {
    id: 'hutang-dividen',
    path: '/buku-pengawasan/hutang-dividen',
    label: 'Hutang Dividen',
    icon: <Coins size={16} />,
    component: HutangDividenPage,
    minLevel: LevelUser_03_Direktur,
    requiresTahunBukuNormal: true,
  },

  // ─────────── Piutang ───────────
  // Piutang Usaha - Multi-Tab Internal Pattern (1 page, 10 tab internal)
  {
    id: 'piutang-usaha',
    path: '/buku-pengawasan/piutang-usaha',
    label: 'Piutang Usaha',
    icon: <Receipt size={16} />,
    component: PiutangUsahaPage,
    minLevel: LevelUser_03_Direktur,
    requiresTahunBukuNormal: true,
  },
  // Penjualan Termin (COA Uang Muka Penjualan 214110001 IDR + 214120101-214120701 MUA) - Multi-Tab 8 mata uang.
  {
    id: 'penjualan-termin',
    path: '/buku-pengawasan/penjualan-termin',
    label: 'Penjualan Termin',
    icon: <Receipt size={16} />,
    component: PenjualanTerminPage,
    minLevel: LevelUser_03_Direktur,
    requiresTahunBukuNormal: true,
  },
  // Pembelian Termin (COA Uang Muka Pembelian 116110001 IDR + 116120101-116120701 MUA) - Multi-Tab 8 mata uang.
  {
    id: 'pembelian-termin',
    path: '/buku-pengawasan/pembelian-termin',
    label: 'Pembelian Termin',
    icon: <Receipt size={16} />,
    component: PembelianTerminPage,
    minLevel: LevelUser_03_Direktur,
    requiresTahunBukuNormal: true,
  },
  {
    id: 'piutang-pihak-ketiga',
    path: '/buku-pengawasan/piutang-pihak-ketiga',
    label: 'Piutang Pihak Ketiga',
    icon: <Handshake size={16} />,
    component: PiutangPihakKetigaPage,
    minLevel: LevelUser_03_Direktur,
    requiresTahunBukuNormal: true,
  },
  {
    id: 'piutang-afiliasi',
    path: '/buku-pengawasan/piutang-afiliasi',
    label: 'Piutang Afiliasi',
    icon: <Building2 size={16} />,
    component: PiutangAfiliasiPage,
    minLevel: LevelUser_03_Direktur,
    requiresTahunBukuNormal: true,
  },
  {
    id: 'piutang-karyawan',
    path: '/buku-pengawasan/piutang-karyawan',
    label: 'Piutang Karyawan',
    icon: <UserSquare2 size={16} />,
    component: PiutangKaryawanPage,
    minLevel: LevelUser_03_Direktur,
    requiresTahunBukuNormal: true,
  },
  {
    id: 'piutang-pemegang-saham',
    path: '/buku-pengawasan/piutang-pemegang-saham',
    label: 'Piutang Pemegang Saham',
    icon: <Briefcase size={16} />,
    component: PiutangPemegangSahamPage,
    minLevel: LevelUser_03_Direktur,
  },
  {
    id: 'piutang-dividen',
    path: '/buku-pengawasan/piutang-dividen',
    label: 'Piutang Dividen',
    icon: <Coins size={16} />,
    component: PiutangDividenPage,
    minLevel: LevelUser_03_Direktur,
    requiresTahunBukuNormal: true,
  },
  {
    id: 'deposit-operasional',
    path: '/buku-pengawasan/deposit-operasional',
    label: 'Deposit Operasional',
    icon: <Coins size={16} />,
    component: DepositOperasionalPage,
    minLevel: LevelUser_03_Direktur,
    requiresTahunBukuNormal: true,
  },

  // ─────────── Hutang Pajak (Multi-Tab Host) ───────────
  {
    id: 'pph-21',
    path: '/pajak/pph-21',
    label: 'PPh Pasal 21',
    icon: <Receipt size={16} />,
    component: HutangPPh21Page,
    minLevel: LevelUser_03_Direktur,
    requiresTahunBukuNormal: true,
  },
  {
    id: 'pph-22-impor',
    path: '/pajak/pph-22-impor',
    label: 'PPh Pasal 22 Impor',
    icon: <Receipt size={16} />,
    component: lazy(() => Promise.resolve({ default: () => <PlaceholderContent title="PPh Pasal 22 Impor" /> })),
    requiresTahunBukuNormal: true,
  },
  {
    id: 'pph-22-lokal',
    path: '/pajak/pph-22-lokal',
    label: 'PPh Pasal 22 Lokal',
    icon: <Receipt size={16} />,
    component: lazy(() => Promise.resolve({ default: () => <PlaceholderContent title="PPh Pasal 22 Lokal" /> })),
    requiresTahunBukuNormal: true,
  },
  {
    id: 'pph-23',
    path: '/pajak/pph-23',
    label: 'PPh Pasal 23',
    icon: <Receipt size={16} />,
    component: HutangPPh23Page,
    minLevel: LevelUser_03_Direktur,
    requiresTahunBukuNormal: true,
  },
  {
    id: 'pph-42',
    path: '/pajak/pph-42',
    label: 'PPh Pasal 4(2)',
    icon: <Receipt size={16} />,
    component: HutangPPh42Page,
    minLevel: LevelUser_03_Direktur,
    requiresTahunBukuNormal: true,
  },
  {
    id: 'pph-25',
    path: '/pajak/pph-25',
    label: 'PPh Pasal 25',
    icon: <Receipt size={16} />,
    component: HutangPPh25Page,
    minLevel: LevelUser_03_Direktur,
    requiresTahunBukuNormal: true,
  },
  {
    id: 'pph-26',
    path: '/pajak/pph-26',
    label: 'PPh Pasal 26',
    icon: <Receipt size={16} />,
    component: HutangPPh26Page,
    minLevel: LevelUser_03_Direktur,
    requiresTahunBukuNormal: true,
  },
  {
    id: 'ppn',
    path: '/pajak/ppn',
    label: 'Pelaporan PPN',
    icon: <Receipt size={16} />,
    component: PelaporanPPNPage,
    minLevel: LevelUser_03_Direktur,
    requiresTahunBukuNormal: true,
  },
  {
    id: 'ketetapan-pajak',
    path: '/pajak/ketetapan',
    label: 'Ketetapan Pajak',
    icon: <Receipt size={16} />,
    component: BukuPengawasanKetetapanPajakPage,
    minLevel: LevelUser_03_Direktur,
    requiresTahunBukuNormal: true,
  },
  {
    id: 'pajak-impor',
    path: '/pajak/impor',
    label: 'Pajak Impor',
    icon: <Receipt size={16} />,
    component: lazy(() => Promise.resolve({ default: () => <PlaceholderContent title="Pajak Impor" /> })),
    requiresTahunBukuNormal: true,
  },

  // ─────────── Bukti Potong PPh (Multi-Tab Host) ───────────
  {
    id: 'bukti-potong-prepaid',
    path: '/pajak/bukti-potong/prepaid',
    label: 'Bukti Potong PPh (Prepaid)',
    icon: <FileText size={16} />,
    component: BuktiPotongPrepaidHost,
    requiresTahunBukuNormal: true,
  },
  {
    id: 'bukti-potong-paid',
    path: '/pajak/bukti-potong/paid',
    label: 'Bukti Potong PPh (Paid)',
    icon: <FileText size={16} />,
    component: BuktiPotongPaidHost,
    requiresTahunBukuNormal: true,
  },

  // ─────────── Aktiva Lainnya ───────────
  {
    id: 'aktiva-lainnya',
    path: '/buku-pengawasan/aktiva-lainnya',
    label: 'Aktiva Lainnya',
    icon: <Package size={16} />,
    component: lazy(() => Promise.resolve({ default: () => <PlaceholderContent title="Aktiva Lainnya" /> })),
    requiresTahunBukuNormal: true,
  },

  // ============================================
  // MANAJEMEN ASSET
  // Single Pages: tiap halaman = 1 tab aplikasi (class berbeda)
  // ============================================
  {
    id: 'penyusutan-asset',
    path: '/manajemen-asset/penyusutan',
    label: 'Daftar Penyusutan Asset Tetap',
    icon: <FileText size={16} />,
    component: DaftarPenyusutanAssetPage,
  },
  {
    id: 'disposal-asset',
    path: '/manajemen-asset/disposal',
    label: 'Buku Disposal Asset Tetap',
    icon: <FileText size={16} />,
    component: lazy(() => Promise.resolve({ default: () => <PlaceholderContent title="Buku Disposal Asset Tetap" /> })),
    requiresTahunBukuNormal: true,
  },
  {
    id: 'amortisasi-biaya',
    path: '/manajemen-asset/amortisasi',
    label: 'Daftar Amortisasi Biaya',
    icon: <FileText size={16} />,
    component: DaftarAmortisasiBiayaPage,
  },

  // ============================================
  // PAJAK (Section 11 di Booku Lama)
  // Fitur baru yang belum ada di Buku Pengawasan
  // ============================================
  {
    id: 'profil-pajak',
    path: '/pajak/profil',
    label: 'Profil Pajak Perusahaan',
    icon: <Building2 size={16} />,
    component: lazy(() => Promise.resolve({ default: () => <PlaceholderContent title="Profil Pajak Perusahaan" /> })),
    requiresTahunBukuNormal: true,
  },
  {
    id: 'perhitungan-pajak-bulanan',
    path: '/pajak/perhitungan-bulanan',
    label: 'Perhitungan Pajak Bulanan',
    icon: <Calculator size={16} />,
    component: lazy(() => Promise.resolve({ default: () => <PlaceholderContent title="Perhitungan Pajak Bulanan" /> })),
    requiresTahunBukuNormal: true,
  },
  {
    id: 'pajak-pph-29',
    path: '/pajak/pph-29',
    label: 'PPh Pasal 29',
    icon: <Receipt size={16} />,
    component: lazy(() => Promise.resolve({ default: () => <PlaceholderContent title="PPh Pasal 29" /> })),
    requiresTahunBukuNormal: true,
  },
  {
    id: 'input-bukti-pbk',
    path: '/pajak/input-pbk',
    label: 'Input Bukti PBk',
    icon: <FileText size={16} />,
    component: lazy(() => Promise.resolve({ default: () => <PlaceholderContent title="Input Bukti PBk" /> })),
    requiresTahunBukuNormal: true,
  },
  {
    // Shortcut: route ini akan auto-buka modal Tambah dan langsung navigate
    // ke /pajak/ketetapan (handled di BukuPengawasanKetetapanPajakPage).
    id: 'input-ketetapan-pajak',
    path: '/pajak/input-ketetapan',
    label: 'Input Ketetapan Pajak',
    icon: <FileText size={16} />,
    component: BukuPengawasanKetetapanPajakPage,
    minLevel: LevelUser_03_Direktur,
    requiresTahunBukuNormal: true,
  },
  {
    id: 'equalisasi-pajak',
    path: '/pajak/equalisasi',
    label: 'Perhitungan Equalisasi Pajak Tahunan',
    icon: <Calculator size={16} />,
    component: lazy(() => Promise.resolve({ default: () => <PlaceholderContent title="Perhitungan Equalisasi Pajak Tahunan" /> })),
    requiresTahunBukuNormal: true,
  },

  // ============================================
  // LAPORAN
  // ============================================

  // Laporan
  {
    id: 'buku-besar',
    path: '/laporan/buku-besar',
    label: 'Buku Besar',
    icon: <BookOpen size={16} />,
    component: BukuBesarHost,
    minLevel: LevelUser_03_Direktur,
  },
  {
    id: 'buku-kas',
    path: '/laporan/buku-kas',
    label: 'Buku Kas',
    icon: <Banknote size={16} />,
    component: BukuKasHost,
    minLevel: LevelUser_03_Direktur,
  },
  {
    id: 'buku-bank',
    path: '/laporan/buku-bank',
    label: 'Buku Bank',
    icon: <Landmark size={16} />,
    component: BukuBankHost,
    minLevel: LevelUser_03_Direktur,
  },
  {
    id: 'buku-cash-advance',
    path: '/laporan/buku-cash-advance',
    label: 'Buku Cash Advance',
    icon: <CreditCard size={16} />,
    component: BukuCashAdvanceHost,
    minLevel: LevelUser_03_Direktur,
  },
  {
    id: 'buku-petty-cash',
    path: '/laporan/buku-petty-cash',
    label: 'Buku Petty Cash',
    icon: <Coins size={16} />,
    component: BukuPettyCashHost,
    minLevel: LevelUser_03_Direktur,
  },

  // Laporan Keuangan
  {
    id: 'laba-rugi-bulanan',
    path: '/laporan/laba-rugi-bulanan',
    label: 'Laporan Laba Rugi Bulanan',
    icon: <FileText size={16} />,
    component: LabaRugiBulananPage,
    minLevel: LevelUser_03_Direktur,
  },
  {
    id: 'neraca-bulanan',
    path: '/laporan/neraca-bulanan',
    label: 'Laporan Neraca Bulanan',
    icon: <FileText size={16} />,
    component: LaporanNeracaBulananPage,
    minLevel: LevelUser_03_Direktur,
  },
  {
    id: 'neraca-lajur',
    path: '/laporan/neraca-lajur',
    label: 'Neraca Lajur',
    icon: <FileText size={16} />,
    component: NeracaLajurPage,
    minLevel: LevelUser_03_Direktur,
  },
  {
    id: 'tutup-buku-tahunan',
    path: '/tutup-buku-tahunan',
    label: 'Tutup Buku Tahunan',
    icon: <FileText size={16} />,
    component: TutupBukuTahunanPage,
    minLevel: LevelUser_03_Direktur,
  },
  {
    id: 'migrasi-coa',
    path: '/migrasi-coa',
    label: 'Migrasi COA 9 Digit',
    icon: <FileText size={16} />,
    component: MigrasiCoaPage,
    minLevel: LevelUser_03_Direktur,
  },
  {
    id: 'laporan-hpp',
    path: '/laporan/hpp',
    label: 'Laporan HPP',
    icon: <FileText size={16} />,
    component: LaporanHppPage,
    minLevel: LevelUser_03_Direktur,
  },
  {
    id: 'laporan-aktivitas-transaksi',
    path: '/laporan/aktivitas-transaksi',
    label: 'Laporan Aktivitas Transaksi',
    icon: <FileText size={16} />,
    component: lazy(() => Promise.resolve({ default: () => <PlaceholderContent title="Laporan Aktivitas Transaksi" /> })),
  },

  // Pengaturan
  {
    id: 'company-profile',
    path: '/pengaturan/company-profile',
    label: 'Company Profile',
    icon: <Briefcase size={16} />,
    component: CompanyProfilePage,
    minLevel: LevelUser_03_Direktur,
  },
  {
    id: 'pengaturan-umum',
    path: '/pengaturan/umum',
    label: 'Pengaturan Umum',
    icon: <Settings size={16} />,
    component: lazy(() => Promise.resolve({ default: () => <PlaceholderContent title="Pengaturan Umum" /> })),
  },
  {
    id: 'pengaturan-database',
    path: '/pengaturan/database',
    label: 'Pengaturan Database',
    icon: <Settings size={16} />,
    component: lazy(() => Promise.resolve({ default: () => <PlaceholderContent title="Pengaturan Database" /> })),
  },

  // User (halaman khusus, diakses via tombol di header)
  {
    id: 'profil',
    path: '/profil',
    label: 'Profil Saya',
    icon: <User size={16} />,
    component: ProfilPage,
    // Semua user bisa akses halaman profil untuk edit data sendiri
  },
];

// ============================================
// HELPER FUNCTIONS
// ============================================

/**
 * Get page definition by path
 */
export function getPageByPath(path: string): PageDefinition | undefined {
  // Handle path with query params
  const cleanPath = path.split('?')[0];
  return pageRegistry.find((p) => p.path === cleanPath);
}

/**
 * Get page definition by id
 */
export function getPageById(id: string): PageDefinition | undefined {
  return pageRegistry.find((p) => p.id === id);
}

// ============================================
// LOADING & PLACEHOLDER
// ============================================

function PageLoading() {
  return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
      <Spin size="large" />
    </div>
  );
}

function PlaceholderContent({ title }: { title: string }) {
  return (
    <div className="content-body">
      <PageHeader
        title={title}
        description="Halaman ini sedang dalam pengembangan"
      />
      <div style={{ padding: '40px', textAlign: 'center', color: 'var(--color-text-secondary)' }}>
        <p>Fitur {title} akan segera hadir.</p>
      </div>
    </div>
  );
}

// ============================================
// PAGE RENDERER COMPONENT
// ============================================

interface PageRendererProps {
  pageId: string;
  isActive: boolean;
}

/**
 * Render a page component by its ID
 */
export function PageRenderer({ pageId, isActive }: PageRendererProps) {
  const page = getPageById(pageId);

  if (!page) {
    return <PlaceholderContent title="Halaman tidak ditemukan" />;
  }

  const PageComponent = page.component;

  return (
    <div style={{ display: isActive ? 'block' : 'none' }}>
      <Suspense fallback={<PageLoading />}>
        <PageComponent />
      </Suspense>
    </div>
  );
}
